import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Grid, Chip, Box, Divider, TextField
} from '@mui/material'
import { FkspRequest } from '../types'
import { fkspStatusChipColor, fkspStatusLabelCs } from '../lib/fkspStatusLabels'
import { format } from 'date-fns'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import AttachFileIcon from '@mui/icons-material/AttachFile'

const FKSP_MAX_BENEFIT_CONTRIB = 4000

function fkspAttachmentHref(url: string): string {
  const t = (url || '').trim()
  if (!t) return '#'
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('/')) return `${window.location.origin}${t}`
  return t
}

function dash(v: unknown): string {
  if (v === null || v === undefined) return '—'
  const s = String(v).trim()
  return s ? s : '—'
}

function moneyKc(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
  if (!Number.isFinite(n)) return '—'
  return `${n.toLocaleString('cs-CZ')} Kč`
}

function Field({
  label,
  children,
  multiline,
}: {
  label: string
  children: React.ReactNode
  multiline?: boolean
}) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        component="div"
        sx={multiline ? { whiteSpace: 'pre-line' } : undefined}
      >
        {children}
      </Typography>
    </Box>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  request: FkspRequest | null
  canApprove: boolean
  onApprove: (id: string, note?: string) => void
  onReject: (id: string, note?: string) => void
  /** Žadatel, podavatel za jiného nebo IT admin může žádost smazat. */
  onDelete?: (id: string) => void
  /** Stažení tiskového PDF (logo školy, přehledné rozložení). */
  onDownloadPdf?: (req: FkspRequest) => void
  loading?: boolean
  /** Větší pole poznámky pro personalistu (uloží se do žádosti a do PDF u personálního oddělení). */
  prominentPersonalistaNote?: boolean
  /**
   * @deprecated PDF je dostupné vždy, pokud je předáno onDownloadPdf.
   * Schválení se v PDF uvádí jen když už proběhlo.
   */
  allowPdfDownload?: boolean
}

export const FkspRequestModal: React.FC<Props> = ({
  open,
  onClose,
  request,
  canApprove,
  onApprove,
  onReject,
  onDelete,
  onDownloadPdf,
  loading,
  prominentPersonalistaNote,
}) => {
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open && request) setNote('')
  }, [open, request?.id])

  if (!request) return null

  const isBenefity = request.type === 'benefity'
  const typeTitle = isBenefity ? 'Benefity (Rekreace)' : 'Připojištění'
  const subjectLabel = isBenefity ? 'Typ benefitu' : 'Druh připojištění'
  const subjectText = dash(request.benefit_type)

  const amountNum =
    request.amount !== null && request.amount !== undefined
      ? Number(request.amount)
      : NaN
  const copay =
    isBenefity && Number.isFinite(amountNum)
      ? Math.max(0, Math.round((amountNum - FKSP_MAX_BENEFIT_CONTRIB) * 100) / 100)
      : null

  const getStatusLabel = (status: string) => (
    <Chip label={fkspStatusLabelCs(status)} color={fkspStatusChipColor(status)} size="small" />
  )

  const handleApprove = () => onApprove(request.id, note)
  const handleReject = () => onReject(request.id, note)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div">
            Detail žádosti FKSP
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {typeTitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {getStatusLabel(request.status)}
          {request.financist_processed_at && (
            <Chip
              label="Zpracováno ekonomem"
              size="small"
              sx={{ bgcolor: '#fff9c4', color: 'text.primary', fontWeight: 600 }}
            />
          )}
          {onDownloadPdf && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => onDownloadPdf(request)}
            >
              PDF (tisk)
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.100',
              }}
            >
              <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 0.75 }}>
                Předmět žádosti — {subjectLabel}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                {subjectText}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                {isBenefity ? (
                  <>
                    <Typography variant="body2">
                      <Box component="span" color="text.secondary">Předpokládaná cena: </Box>
                      <strong>{moneyKc(request.amount)}</strong>
                    </Typography>
                    {copay !== null && (
                      <Typography variant="body2">
                        <Box component="span" color="text.secondary">Doplatek žadatele (nad 4 000 Kč): </Box>
                        <strong>{moneyKc(copay)}</strong>
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2">
                    <Box component="span" color="text.secondary">Požadovaný příspěvek FKSP: </Box>
                    <strong>{moneyKc(request.fksp_contribution)}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Field label="Žadatel">{dash(request.requester_name)}</Field>
            <Field label="Osobní číslo">{dash(request.personal_number)}</Field>
            <Field label="Bydliště">{dash(request.requester_address)}</Field>
            {request.created_by_name &&
              request.created_by_id &&
              request.created_by_id !== request.requester_id && (
                <Field label="Podáno jménem žadatele">{dash(request.created_by_name)}</Field>
              )}
            <Field label="Datum podání">
              {request.created_at
                ? format(new Date(request.created_at), 'dd.MM.yyyy HH:mm')
                : '—'}
            </Field>
          </Grid>

          <Grid item xs={12} md={6}>
            <Field label="Typ žádosti">{typeTitle}</Field>
            <Field label="Stav schvalování">{fkspStatusLabelCs(request.status)}</Field>
            {request.financist_doplaceno != null && String(request.financist_doplaceno) !== '' && (
              <Field label="Doplaceno (ekonom)">{moneyKc(request.financist_doplaceno)}</Field>
            )}
            {request.financist_datum && (
              <Field label="Datum zaplacení (ekonom)">
                {format(new Date(request.financist_datum), 'dd.MM.yyyy')}
              </Field>
            )}
            {request.financist_note && (
              <Field label="Poznámka ekonoma" multiline>
                {request.financist_note}
              </Field>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {isBenefity ? (
            <>
              <Grid item xs={12} md={6}>
                <Field label="Popis výdajů / specifikace" multiline>
                  {dash(request.expenses_description)}
                </Field>
                {request.description_link ? (
                  <Field label="Odkaz na specifikaci">
                    <a href={request.description_link} target="_blank" rel="noreferrer">
                      {request.description_link}
                    </a>
                  </Field>
                ) : (
                  <Field label="Odkaz na specifikaci">—</Field>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Poskytovatel služby">{dash(request.provider_name)}</Field>
                <Field label="Adresa poskytovatele">{dash(request.provider_address)}</Field>
                <Field label="IČO">{dash(request.provider_ico)}</Field>
                <Field label="Kontakt poskytovatele">
                  {dash(
                    [request.provider_email, request.provider_phone].filter(Boolean).join(', ') || null,
                  )}
                </Field>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <Field label="Penzijní společnost / pojišťovna">
                  {dash(request.institution_name)}
                </Field>
                <Field label="Číslo smlouvy">{dash(request.contract_number)}</Field>
                <Field label="Datum uzavření smlouvy">
                  {request.contract_date
                    ? format(new Date(request.contract_date), 'dd.MM.yyyy')
                    : '—'}
                </Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field label="Číslo účtu poskytovatele">{dash(request.account_number)}</Field>
                <Field label="Variabilní symbol">{dash(request.variable_symbol)}</Field>
              </Grid>
            </>
          )}

          {request.contract_attachment_url && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Příloha — smlouva
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon />}
                component="a"
                href={fkspAttachmentHref(request.contract_attachment_url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {request.contract_attachment_filename || 'Stáhnout / zobrazit sken smlouvy'}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {prominentPersonalistaNote && canApprove && (
            <Grid item xs={12}>
              <TextField
                label="Poznámka personalisty"
                placeholder="Volitelný komentář k žádosti (schválení / zamítnutí)…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                helperText="Poznámka se uloží k žádosti a v benefitech se vloží do tiskového PDF u personálního oddělení."
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Průběh schvalování
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <strong>Personalista:</strong>{' '}
                {getStatusLabel(
                  request.personalista_status
                    ? `personalista_${request.personalista_status}`
                    : 'personalista_pending',
                )}
                {request.personalista_name && ` (${request.personalista_name})`}
                {request.personalista_at && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {format(new Date(request.personalista_at), 'dd.MM.yyyy HH:mm')}
                  </Typography>
                )}
                {request.personalista_note && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    Poznámka: {request.personalista_note}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <strong>ZO ČMOS:</strong>{' '}
                {getStatusLabel(
                  request.zocmos_status ? `zocmos_${request.zocmos_status}` : 'zocmos_pending',
                )}
                {request.zocmos_name && ` (${request.zocmos_name})`}
                {request.zocmos_at && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {format(new Date(request.zocmos_at), 'dd.MM.yyyy HH:mm')}
                  </Typography>
                )}
                {request.zocmos_note && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    Poznámka: {request.zocmos_note}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <strong>Ředitel:</strong>{' '}
                {getStatusLabel(
                  request.director_status
                    ? `director_${request.director_status}`
                    : 'director_pending',
                )}
                {request.director_name && ` (${request.director_name})`}
                {request.director_at && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {format(new Date(request.director_at), 'dd.MM.yyyy HH:mm')}
                  </Typography>
                )}
                {request.director_note && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    Poznámka: {request.director_note}
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: canApprove ? 'space-between' : 'flex-end',
          alignItems: 'center',
        }}
      >
        {canApprove ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {onDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => onDelete(request.id)}
                  disabled={loading}
                >
                  Smazat
                </Button>
              )}
            </Box>
            {!prominentPersonalistaNote && (
              <TextField
                size="small"
                placeholder="Volitelná poznámka..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 200 }}
              />
            )}
            <Button variant="outlined" color="error" onClick={handleReject} disabled={loading}>
              Zamítnout
            </Button>
            <Button variant="contained" color="success" onClick={handleApprove} disabled={loading}>
              Schválit
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => onDelete(request.id)}
                disabled={loading}
              >
                Smazat
              </Button>
            )}
            <Button onClick={onClose}>Zavřít</Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  )
}
